import React from "react";
import { get } from "lodash";
import { COMPONENT_MODES, CTA_SIZE, CTA_TYPE } from "../../../data/constants";
import { isAddNewMode, isViewMode } from "../../../helpers/catalog-helper";
import CustomTextField from "../../../components/reusable-ui/textfield-box";
import {
  allowEverything,
  removeSpecialCharacters,
  removeSpecialCharAndNumbers,
} from "../../../utils";
import CtaComponent from "../../../components/reusable-ui/cta";
import { useSelector } from "react-redux";
import { RootState } from "../../../redux/store";

interface Props {
  curData?: any;
  modeConfig?: any;
  setModeConfig?: any;
  updateCurDataInParentState?: any;
  errorMessageMap?: any;
  curDataId?: string;
  isChangesMade?: boolean;
  onDiscardCtaClick?: any;
  onSaveCtaClick?: any;
}

const BrandsDetailsForm = (props: Props) => {
  const {
    curData,
    curDataId = "",
    isChangesMade,
    modeConfig,
    setModeConfig,
    updateCurDataInParentState,
    errorMessageMap,
    onDiscardCtaClick,
    onSaveCtaClick,
  } = props;

  const brandsDataMap = useSelector(
    (state: RootState) => state.brandsReducer.brandsData
  );

  const TEXT_FIELD_CONFIG = {
    name: {
      placeHolder: "Enter Brand Name",
      fieldLabel: "Name",
      value: get(curData, "name"),
      boxWidth: "w-full mb-6",
      key: "name",
      regexCheck: removeSpecialCharacters,
      isMandatory: true,
      maxLength: 25,
    },
    description: {
      placeHolder: "Enter Description",
      fieldLabel: "Description",
      regexCheck: allowEverything,
      value: get(curData, "description"),
      key: "description",
      rows: 2,
      maxLength: 100,
    },
  };

  function handleInputChange(value: any, fieldToUpdate: string) {
    updateCurDataInParentState(fieldToUpdate, value);
  }

  /**
   * This function will render the customTextField input with the help of textFieldConfig
   * @param textFieldConfig
   */
  function renderTextField(textFieldConfig: any): React.JSX.Element {
    return (
      <CustomTextField
        textBoxLabel={textFieldConfig.fieldLabel}
        placeholder={textFieldConfig.placeHolder}
        regexCheck={textFieldConfig.regexCheck || removeSpecialCharAndNumbers}
        handleChange={(value: any) =>
          handleInputChange(value, textFieldConfig.key)
        }
        customClass={`min-h-[44px] flex items-center px-16 text-16 font-normal
          ${textFieldConfig.customClass || ""}`}
        parentClass={`${textFieldConfig.boxWidth || ""} ${
          textFieldConfig.parentClass || ""
        }`}
        value={textFieldConfig.value}
        rows={textFieldConfig.rows}
        isMandatory={!!textFieldConfig.isMandatory}
        errorMessage={errorMessageMap[textFieldConfig.key] || ""}
        iconClass={textFieldConfig.iconClass || ""}
        isDisabled={isViewMode(modeConfig?.type)}
        maxLength={textFieldConfig?.maxLength}
      ></CustomTextField>
    );
  }

  return (
    <section
      className={`${
        isAddNewMode(modeConfig.type) ? "w-full" : "w-[50%] p-[24px] pt-0"
      }`}
    >
      {!isAddNewMode(modeConfig.type) && (
        <div className="flex items-center justify-between">
          <div className="text-[20px] font-normal py-[24px] capitalize">
            {brandsDataMap[curDataId]?.name}
          </div>
          {isViewMode(modeConfig?.type) && (
            <div
              className="flex cursor-pointer"
              onClick={() =>
                setModeConfig({
                  ...modeConfig,
                  type: COMPONENT_MODES.editMode,
                })
              }
            >
              <div className="rista-sprite ic-pencil-blue"></div>
              <p className="text-14 text-blue font-semibold ml-4">Edit</p>
            </div>
          )}
        </div>
      )}
      <div
        className={`bg-white p-[24px] ${
          !isAddNewMode(modeConfig.type)
            ? "h-[calc(100vh-243px)] overflow-y-auto pb-[66px]"
            : ""
        }`}
      >
        {renderTextField(TEXT_FIELD_CONFIG.name)}
        {renderTextField(TEXT_FIELD_CONFIG.description)}
      </div>

      {modeConfig.type === COMPONENT_MODES.editMode && (
        <div className="fixed bottom-[0px] z-[111] bg-white h-[70px] px-24 flex justify-center items-center overflow-hidden right-0 w-[calc(50%-48px)] border-t-[1px] border-[#f5f5f5]">
          <CtaComponent
            ctaText={"Discard"}
            ctaType={CTA_TYPE.secondary_cta}
            ctaSize={CTA_SIZE.medium}
            onCtaClick={onDiscardCtaClick}
            customClass={"w-[calc(50%-10px)] mr-10"}
          ></CtaComponent>
          <CtaComponent
            ctaText={"Save Changes"}
            ctaType={CTA_TYPE.active_cta}
            ctaSize={CTA_SIZE.medium}
            onCtaClick={onSaveCtaClick}
            customClass={"w-[calc(50%-10px)] ml-10"}
            isDisabled={!isChangesMade}
          ></CtaComponent>
        </div>
      )}
    </section>
  );
};

export default BrandsDetailsForm;
